import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ValidationRefundNotice from "@/components/ValidationRefundNotice";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Briefcase, MessageSquare, CheckCircle2, XCircle, Crown, ArrowUpRight, BookOpen, Plus, Edit, Lock, Sparkles, Camera } from "lucide-react";
import MissionVoiceSynthesis from "@/components/MissionVoiceSynthesis";
import RecentLoginsWidget from "@/components/RecentLoginsWidget";
import ExpertHandoffsPanel from "@/components/ExpertHandoffsPanel";
import ExpertVideoUploadCard from "@/components/ExpertVideoUploadCard";

const STATUS_LABEL = {
  brouillon: "Brouillon", publiee: "Publiée", en_attente: "En attente",
  acceptee: "Acceptée", refusee: "Refusée", en_cours: "En cours", terminee: "Terminée", annulee: "Annulée",
};

const ARTICLE_STATUS_LABEL = {
  draft: "Brouillon",
  pending: "En relecture",
  published: "Publié",
  rejected: "Refusé",
};
const ARTICLE_STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-700",
  pending: "bg-amber-100 text-amber-800",
  published: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

export default function ExpertDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [missions, setMissions] = useState([]);
  const [articles, setArticles] = useState([]);

  const refresh = useCallback(() => {
    apiClient.get("/experts/me").then(r => setProfile(r.data)).catch(() => setProfile(null));
    apiClient.get("/missions").then(r => setMissions(r.data));
    apiClient.get("/articles/mine").then(r => setArticles(r.data)).catch(() => setArticles([]));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const updateStatus = async (id, status) => {
    try {
      await apiClient.patch(`/missions/${id}/status`, { status });
      toast.success("Statut actualisé");
      refresh();
    } catch (e) {
      toast.error("La mise à jour a échoué");
    }
  };

  const unlockMission = async (missionId) => {
    try {
      const { data } = await apiClient.post(`/missions/${missionId}/unlock-checkout`, {
        origin_url: window.location.origin,
      });
      if (data.already_unlocked) {
        toast.success("Ce dossier est déjà débloqué");
        refresh();
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Le déblocage a échoué");
    }
  };

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <Navbar variant="light" />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2">Espace personnel · Spécialiste référencé</div>
        <h1 className="font-serif text-4xl text-[#0B3A2A] tracking-tight mb-2">Bienvenue, {user?.first_name}</h1>
        {profile ? (
          <>
            <div className="flex items-center gap-2 mb-6 text-sm">
              {profile.verified ? (
                <Badge className="bg-[#7A9B7E]/15 text-[#5C7D62] rounded-full"><ShieldCheck className="w-3 h-3 mr-1" />Profil référencé</Badge>
              ) : (
                <Badge className="bg-[#C9A961]/15 text-[#8A6E3A] rounded-full">En cours de validation par CONFILEX</Badge>
              )}
              <Badge className="bg-[#C9A961]/10 text-[#C9A961] border border-[#C9A961]/30 rounded-full capitalize">{profile.subscription}</Badge>
            </div>
            {!profile.verified && (
              <ValidationRefundNotice className="mb-10" />
            )}
          </>
        ) : (
          <Card className="p-6 mb-10 rounded-2xl border-[#C9A961]/30 bg-[#FAF6EE]">
            <p className="text-slate-700 mb-3">Votre profil de spécialiste n'est pas encore complété. Veuillez finaliser votre candidature.</p>
            <Button asChild className="rounded-full bg-[#0B3A2A] text-white">
              <Link to="/expert/apply">Compléter mon profil</Link>
            </Button>
          </Card>
        )}

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {[
            { l: "Demandes reçues", v: missions.length },
            { l: "En cours", v: missions.filter(m => ["acceptee","en_cours"].includes(m.status)).length },
            { l: "Interactions", v: profile?.reviews_count ?? "—" },
          ].map(s => (
            <Card key={s.l} className="p-6 rounded-2xl border-slate-100">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{s.l}</div>
              <div className="font-serif text-3xl text-[#0B3A2A]">{s.v}</div>
            </Card>
          ))}
        </div>

        <Card className="p-6 rounded-2xl border-[#C9A961]/40 bg-gradient-to-r from-[#FAF7F1] to-white mb-6" data-testid="photo-studio-cta">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-[#C9A961]/20 border border-[#C9A961]/40 flex items-center justify-center shrink-0">
                <Camera className="w-6 h-6 text-[#C9A961]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-1">Studio IA</div>
                <h3 className="font-serif text-xl text-[#0B3A2A]">Créer ma photo professionnelle</h3>
                <p className="text-sm text-slate-600 mt-1 max-w-md">Obtenez en quelques secondes une photo institutionnelle conforme aux standards CONFILEX, sans passer par un photographe.</p>
              </div>
            </div>
            <Button asChild className="rounded-full bg-[#0B3A2A] text-white hover:bg-[#0B3A2A]" data-testid="goto-photo-studio">
              <Link to="/expert/photo">Lancer le studio <ArrowUpRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl border-[#0B3A2A]/15 bg-gradient-to-r from-[#0B3A2A] to-[#0B3A2A] text-white mb-10" data-testid="rdv-cta-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-1">Visioconférences</div>
              <h3 className="font-serif text-xl">Vos demandes de rendez-vous</h3>
              <p className="text-sm text-white/70 mt-1">Acceptez ou refusez les consultations clients en un clic.</p>
            </div>
            <Button asChild className="rounded-full bg-white text-[#0B3A2A] hover:bg-white/90" data-testid="goto-rdv-dashboard">
              <Link to="/expert/dashboard/rdv">Gérer mes rendez-vous <ArrowUpRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl border-[#C9A961]/30 bg-white mb-10" data-testid="network-cta-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-1">Réseau professionnel · inclus</div>
              <h3 className="font-serif text-xl text-[#0B3A2A]">Le réseau des spécialistes référencés CONFILEX</h3>
              <p className="text-sm text-[#0B3A2A]/70 mt-1 max-w-2xl">
                Identifiez des confrères ou des professionnels complémentaires (notaires, fiscalistes, CGP, experts immobiliers…) et échangez gratuitement dans un espace sécurisé. Aucun paiement, aucune commission.
              </p>
            </div>
            <Button asChild className="rounded-full bg-[#0B3A2A] text-white hover:bg-[#0B3A2A]" data-testid="goto-expert-network">
              <Link to="/expert/network">Ouvrir le réseau <ArrowUpRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </Card>

        {profile && (
          <Card className="p-6 rounded-2xl border-[#C9A961]/30 bg-white mb-10" data-testid="subscription-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#C9A961]/10 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-[#C9A961]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#C9A961] mb-1">Référencement</div>
                  <div className="font-serif text-xl text-[#0B3A2A] capitalize">{profile.subscription || "Standard"}</div>
                  {profile.subscription_active_until && (
                    <div className="text-xs text-slate-500 mt-1">Actif jusqu'au {new Date(profile.subscription_active_until).toLocaleDateString("fr-FR")}</div>
                  )}
                </div>
              </div>
              <Button asChild className="rounded-full bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="upgrade-btn">
                <Link to="/pricing">Faire évoluer mon référencement <ArrowUpRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </Card>
        )}

        <div className="mt-10 mb-10">
          <ExpertHandoffsPanel />
        </div>

        <div className="mt-10 mb-10">
          <ExpertVideoUploadCard />
        </div>

        <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Demandes & dossiers</h2>
        {missions.length === 0 ? (
          <p className="text-slate-500">Aucune demande à traiter pour le moment.</p>
        ) : (
          <div className="space-y-3">
            {missions.map(m => (
              <Card key={m.id} className={`p-6 rounded-2xl ${m.locked ? "border-amber-200 bg-amber-50/30" : "border-slate-100"}`} data-testid={`expert-mission-${m.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="font-serif text-lg text-[#0B3A2A]">{m.title}</h3>
                      <Badge className="bg-slate-100 text-slate-700 rounded-full">{STATUS_LABEL[m.status]}</Badge>
                      {m.locked && (
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-200 rounded-full text-[10px]" data-testid={`lock-badge-${m.id}`}>
                          <Lock className="w-3 h-3 mr-1" />Coordonnées verrouillées
                        </Badge>
                      )}
                      {!m.locked && m.unlock_method && (
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[10px]">
                          {m.unlock_method === "subscription" ? "Accès abonnement" :
                           m.unlock_method === "consent" ? "Accès consenti" :
                           m.unlock_method === "paid" ? "Accès débloqué" : "Accès"}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-2">{m.description || "—"}</p>
                    <div className="text-xs text-slate-400">
                      {m.client_name}
                      {m.locked && <span className="text-amber-700"> · prénom masqué</span>}
                      {" "}· {m.category} · {new Date(m.created_at).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {m.locked ? (
                      <Button
                        size="sm"
                        className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold"
                        onClick={() => unlockMission(m.id)}
                        data-testid={`unlock-${m.id}`}
                      >
                        <Sparkles className="w-4 h-4 mr-1.5" />
                        Débloquer ({m.unlock_fee_eur} €)
                      </Button>
                    ) : (
                      <Button asChild variant="outline" size="sm" className="rounded-full">
                        <Link to={`/messages/${m.id}`}><MessageSquare className="w-4 h-4 mr-1" />Échange</Link>
                      </Button>
                    )}
                    {!m.locked && (m.status === "publiee" || m.status === "en_attente") && (
                      <>
                        <Button size="sm" className="rounded-full bg-[#7A9B7E] hover:bg-[#93B19A]"
                          onClick={() => updateStatus(m.id, "acceptee")} data-testid={`accept-${m.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" />Accepter le dossier
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full"
                          onClick={() => updateStatus(m.id, "refusee")} data-testid={`refuse-${m.id}`}>
                          <XCircle className="w-4 h-4 mr-1" />Décliner
                        </Button>
                      </>
                    )}
                    {m.status === "acceptee" && (
                      <Button size="sm" className="rounded-full bg-[#0B3A2A] hover:bg-[#0B3A2A]"
                        onClick={() => updateStatus(m.id, "en_cours")}>Engager le dossier</Button>
                    )}
                    {m.status === "en_cours" && (
                      <Button size="sm" className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A]"
                        onClick={() => updateStatus(m.id, "terminee")}>Clôturer</Button>
                    )}
                  </div>
                </div>
                {/* Synthèse vocale validée par le client (le cas échéant) */}
                {!m.locked && m.voice_synthesis_call_id && (
                  <div className="mt-4">
                    <MissionVoiceSynthesis missionId={m.id} viewer="expert" />
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Articles éditoriaux */}
        <div className="mt-14" data-testid="articles-panel">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-1.5 flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> Cahier éditorial
              </div>
              <h2 className="font-serif text-2xl text-[#0B3A2A]">Vos articles</h2>
            </div>
            <Button asChild className="rounded-full bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="article-new">
              <Link to="/expert/articles/new"><Plus className="w-4 h-4 mr-2" /> Nouvel article</Link>
            </Button>
          </div>

          {articles.length === 0 ? (
            <Card className="p-8 rounded-2xl border-slate-100 text-center">
              <p className="text-slate-600 mb-4">Vous n'avez pas encore publié d'analyse. Partagez votre expertise auprès du réseau CONFILEX.</p>
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/expert/articles/new">Rédiger un premier article</Link>
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {articles.map(a => (
                <Card key={a.id} className="p-5 rounded-2xl border-slate-100" data-testid={`article-row-${a.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <h3 className="font-serif text-lg text-[#0B3A2A] truncate">{a.title || "Sans titre"}</h3>
                        <Badge className={`rounded-full text-xs ${ARTICLE_STATUS_BADGE[a.status]}`}>{ARTICLE_STATUS_LABEL[a.status]}</Badge>
                      </div>
                      {a.excerpt && <p className="text-sm text-slate-500 line-clamp-2 mb-1.5">{a.excerpt}</p>}
                      <div className="text-xs text-slate-400">
                        Mis à jour le {new Date(a.updated_at).toLocaleDateString("fr-FR")}
                        {a.view_count ? ` · ${a.view_count} vues` : ""}
                      </div>
                      {a.status === "rejected" && a.rejection_reason && (
                        <div className="text-xs text-rose-700 mt-1.5">Retour éditorial : {a.rejection_reason}</div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {a.status === "published" && (
                        <Button asChild variant="outline" size="sm" className="rounded-full">
                          <Link to={`/insights/${a.slug}`} target="_blank" rel="noreferrer">Consulter</Link>
                        </Button>
                      )}
                      <Button asChild variant="outline" size="sm" className="rounded-full" data-testid={`article-edit-${a.id}`}>
                        <Link to={`/expert/articles/${a.id}`}>
                          <Edit className="w-4 h-4 mr-1.5" /> {a.status === "published" || a.status === "pending" ? "Voir" : "Éditer"}
                        </Link>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Audit de connexion — widget « Dernières connexions » */}
        <div className="mt-10">
          <RecentLoginsWidget limit={5} />
        </div>
      </div>
      <Footer />
    </div>
  );
}
